import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MdLocationOn } from 'react-icons/md';
import {
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaChevronLeft,
  FaRegClock,
  FaRegCheckCircle,
  FaRegFlag,
  FaQrcode,
  FaStar,
  FaUserFriends,
  FaLayerGroup,
  FaRegCopy,
  FaCheck,
} from 'react-icons/fa';
import { BiExpand } from 'react-icons/bi';
import NumberFormat from 'react-number-format';
import { CircularProgress } from '@material-ui/core';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import currencySymbol from '../../../../../helpers/currencySymbol';
import ImageGalleryModal from '../../../../ImageGallery/ImageGalleryModal';
import TourDetails from '../../TourDetails';
import TourBadges from '../TourBadges';
import { tourDetailsRequest } from '../../../../../store/slices/tour';
import { SearchStatus } from '../../../../../constants';
import useScrollToElement from '../../../../../hooks/useScrollToElement';
import getTourOptions from '../../../../../helpers/getTourOptions';
import TourAddUpdateToBrochure from '../../../AddUpdateBrochure/TourAddUpdateToBrochure';
import RemoveFromCompare from '../../../RemoveFromCompare';

const twoLineClamp = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const TourItineraryContent = ({
  itinerary,
  tripId,
  doHaveBorderAround,
  wrapperClassName,
  doShowPrice,
  isBookingOpen,
  isBookingDisabled,
  onToggleBooking,
  onMouseEnter,
  onMouseLeave,
  doScrollToTour,
  isExpanded,
  doShowAvailabilityButton,
  doShowDetailsButton,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { travelAgency = {} } = useSelector((state) => state.user);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const details = useSelector((state) => state.tour.details[itinerary.productId]);

  const isBrochurePage = (router?.pathname || '').indexOf('brochure') > -1;
  const doExpandByDefault =
    !!travelAgency?.settings?.brochureSettings?.doExpandItineraryDetailsByDefault && isBrochurePage;
  const [isDetailsOpen, setIsDetailsOpen] = useState(doExpandByDefault);
  const [isProductIdCopied, setIsProductIdCopied] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const tourItineraryEl = useScrollToElement({ value: doScrollToTour && isExpanded.value });

  const requestDetails = () => {
    const isLoaded = !!details && (!!details.product || details.status === SearchStatus.SEARCHING);
    if (itinerary.productId && !isLoaded) {
      dispatch(tourDetailsRequest({ productId: itinerary.productId }));
    }
  };

  const handleDetailsClick = () => {
    setIsDetailsOpen((v) => !v);
    if (!isDetailsOpen) {
      requestDetails();
    }
  };

  const handleImageClick = () => {
    requestDetails();
    setIsGalleryOpen(true);
  };

  const handleCopyProductId = () => {
    navigator.clipboard
      ?.writeText(itinerary.productId)
      .then(() => {
        setIsProductIdCopied(true);
        setTimeout(() => setIsProductIdCopied(false), 2000);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (isExpanded.value) {
      setIsDetailsOpen(true);
      requestDetails();
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isExpanded.value]);

  const handleMouseEnter = () => {
    if (onMouseEnter) {
      onMouseEnter(itinerary.productId);
    }
  };

  const handleMouseLeave = () => {
    if (onMouseLeave) {
      onMouseLeave(null);
    }
  };

  const tour = itinerary.tour || {};
  const activities = (tour.activities || []).slice(0, 2).join(' & ');
  const options = getTourOptions(tour.rates);
  const facts = [
    tour.productType && { icon: <FaUserFriends size="15" />, text: tour.productType },
    tour.confirmationType === 'Automatic' && {
      icon: <FaRegFlag size="15" />,
      text: 'Live availability',
    },
    itinerary.destination && { icon: <MdLocationOn size="18" />, text: itinerary.destination },
    activities && { icon: <FaRegCheckCircle size="15" />, text: activities },
    tour.durationText && {
      icon: <FaRegClock size="15" />,
      label: 'Duration:',
      text: tour.durationText,
    },
    options.length > 1 && {
      icon: <FaLayerGroup size="15" />,
      label: 'Options:',
      text: options.join(', '),
    },
  ].filter(Boolean);

  const rating = tour.rating > 0 ? Number(tour.rating).toFixed(1) : null;
  const filledStars = rating ? Math.round(Number(rating)) : 0;
  const galleryImages = details?.product?.images?.length
    ? details.product.images
    : tour.images || [];
  const isGalleryLoading =
    isGalleryOpen && !galleryImages.length && details?.status === SearchStatus.SEARCHING;

  return (
    <div
      ref={tourItineraryEl}
      className={clsx('flex flex-col bg-white', wrapperClassName, {
        border: doHaveBorderAround,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div>
        <div className="flex flex-wrap">
          <div className="w-24 md:w-64 flex-shrink-0 mr-4">
            {tour.primaryImageUrl ? (
              <div className="relative">
                <img
                  src={tour.primaryImageUrl}
                  alt={tour.name}
                  className="email-img w-full h-24 md:h-40 object-cover rounded"
                />
                <button
                  type="button"
                  title="View photos"
                  className="email-hidden-field absolute inset-0 w-full h-full focus:outline-none"
                  onClick={handleImageClick}
                >
                  <span className="absolute top-0 left-0 m-2 bg-black bg-opacity-70 text-white rounded-lg p-1">
                    {isGalleryLoading ? (
                      <CircularProgress color="inherit" size={14} />
                    ) : (
                      <BiExpand />
                    )}
                  </span>
                </button>
              </div>
            ) : (
              <div className="email-hidden-field w-full h-24 md:h-40 rounded bg-gray-100" />
            )}

            {isGalleryOpen && galleryImages.length > 0 && (
              <ImageGalleryModal
                doShow
                images={galleryImages}
                initialIndex={0}
                title={tour.name}
                onClose={() => setIsGalleryOpen(false)}
              />
            )}

            {(itinerary.productId || rating) && (
              <div className="mt-1 flex flex-wrap items-center justify-between gap-y-1 text-xs">
                {itinerary.productId && (
                  <span
                    className="flex items-center min-w-0 text-gray-700"
                    title={`Tour ID: ${itinerary.productId}`}
                  >
                    <FaQrcode className="mr-1 text-gray-600 flex-shrink-0" />
                    <span className="font-medium mr-1 flex-shrink-0">Tour ID:</span>
                    <span className="truncate">{itinerary.productId}</span>
                    <button
                      type="button"
                      className="email-hidden-field ml-1 text-gray-600 focus:outline-none flex-shrink-0"
                      title={isProductIdCopied ? 'Copied' : 'Copy tour ID'}
                      onClick={handleCopyProductId}
                    >
                      {isProductIdCopied ? <FaCheck /> : <FaRegCopy />}
                    </button>
                  </span>
                )}
                {rating && (
                  <span className="flex items-center flex-shrink-0 ml-0 md:ml-2">
                    <span className="font-semibold mr-1">{rating}</span>
                    {[0, 1, 2, 3, 4].map((index) => (
                      <FaStar
                        key={index}
                        size="11"
                        className={index < filledStars ? 'text-theme' : 'text-gray-300'}
                      />
                    ))}
                    {tour.reviewCount > 0 && (
                      <span className="text-gray-600 ml-1">({tour.reviewCount})</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div
              title={tour.name}
              className="font-semibold font-subHeading text-subHeading leading-snug"
              style={twoLineClamp}
            >
              {tour.name}
            </div>

            <div className="mt-1 text-sm font-smallerHeading text-gray-800">
              {facts.map((fact) => (
                <div
                  key={fact.text}
                  title={[fact.label, fact.text].filter(Boolean).join(' ')}
                  className="flex items-center truncate leading-relaxed"
                >
                  <span className="mr-2 w-5 text-gray-600 flex-shrink-0 flex justify-center">
                    {fact.icon}
                  </span>
                  {fact.label && (
                    <span className="font-medium mr-1 flex-shrink-0">{fact.label}</span>
                  )}
                  <span className="truncate">{fact.text}</span>
                </div>
              ))}
            </div>

            <TourBadges itinerary={itinerary} className="flex-wrap md:flex-no-wrap mt-2" />
          </div>

          <div className="relative flex flex-col flex-shrink-0 w-full md:w-auto mt-3 md:mt-0 md:ml-3 text-right whitespace-no-wrap">
            {doShowPrice && itinerary.selectedSlot?.price > 0 && (
              <div className="leading-tight">
                <span className="text-smallerHeading font-smallerHeading font-bold">From</span>{' '}
                <span className="text-xl font-bold">
                  <NumberFormat
                    value={itinerary.selectedSlot.price}
                    displayType="text"
                    thousandSeparator
                    prefix={currencySymbol(itinerary.cost.currencyCode)}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </span>
              </div>
            )}

            {doShowAvailabilityButton && (
              <button
                type="button"
                className={clsx(
                  'mt-2 py-1 px-3 text-sm rounded font-bold focus:outline-none whitespace-no-wrap',
                  {
                    'bg-theme text-white': !isBookingDisabled,
                    'bg-gray-200 text-gray-500 cursor-not-allowed': isBookingDisabled,
                  },
                )}
                disabled={isBookingDisabled}
                onClick={onToggleBooking}
              >
                <span className="flex items-center justify-center gap-1">
                  <FaRegClock />
                  {isBookingOpen ? 'Hide availability' : 'View availability'}
                  {isBookingOpen ? <FaChevronLeft /> : <FaChevronRight />}
                </span>
              </button>
            )}

            {doShowDetailsButton && (
              <button
                type="button"
                className="text-theme mt-1 focus:outline-none block w-full text-right"
                onClick={handleDetailsClick}
              >
                {isDetailsOpen ? 'Hide Details ' : 'View Details '}
                {isDetailsOpen ? (
                  <FaChevronUp className="inline-block" />
                ) : (
                  <FaChevronDown className="inline-block" />
                )}
              </button>
            )}

            {isViewingCompare ? (
              <div className="static md:absolute bottom-0 right-0 mt-2 md:mt-0 whitespace-nowrap text-right">
                <RemoveFromCompare itineraryId={itinerary.uniqueKey} itinerary={itinerary} />
              </div>
            ) : (
              <TourAddUpdateToBrochure
                tripId={tripId || itinerary.productId}
                tourItinerary={itinerary}
                className="static md:absolute bottom-0 right-0 mt-2 md:mt-0 whitespace-nowrap text-right"
              />
            )}
          </div>
        </div>

        {isDetailsOpen && (
          <div className="mt-3 border-t pt-3">
            <TourDetails itinerary={itinerary} details={details} />
          </div>
        )}
      </div>
    </div>
  );
};

TourItineraryContent.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  tripId: PropTypes.string,
  doHaveBorderAround: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  doShowPrice: PropTypes.bool,
  isBookingOpen: PropTypes.bool,
  isBookingDisabled: PropTypes.bool,
  onToggleBooking: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  doScrollToTour: PropTypes.bool,
  isExpanded: PropTypes.shape({
    value: PropTypes.bool,
  }),
  doShowAvailabilityButton: PropTypes.bool,
  doShowDetailsButton: PropTypes.bool,
};

TourItineraryContent.defaultProps = {
  tripId: undefined,
  doHaveBorderAround: true,
  wrapperClassName: 'px-4 py-3 mb-2 rounded-lg',
  doShowPrice: true,
  isBookingOpen: false,
  isBookingDisabled: false,
  onToggleBooking: () => {},
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  doScrollToTour: true,
  isExpanded: { value: false },
  doShowAvailabilityButton: true,
  doShowDetailsButton: true,
};

export default TourItineraryContent;
